import { Location, NavigateFunction, useLocation, useNavigate } from 'react-router-dom';

export interface BackgroundState {
    background?: Location;
}

/** Walk nested modal stacks to the original frozen page (e.g. gradebook). */
export const resolveModalBackground = (location: Location): Location => {
    const parent = (location.state as BackgroundState | null)?.background;
    if (!parent) {
        return location;
    }
    if (hasModalBackground(parent)) {
        return resolveModalBackground(parent);
    }
    return parent;
};

export const hasModalBackground = (location: Location): boolean => {
    return Boolean((location.state as BackgroundState | null)?.background);
};

export const buildBackgroundPath = (background: Location): string => {
    return `${background.pathname}${background.search ?? ''}${background.hash ?? ''}`;
};

/** Leave a modal route and return to its frozen background page (replace, not push). */
export const exitToBackgroundPage = (
    navigate: NavigateFunction,
    location: Location,
    fallbackPath: string = '/',
) => {
    if (hasModalBackground(location)) {
        const background = resolveModalBackground(location);
        navigate(buildBackgroundPath(background), {
            replace: true,
            state: background.state ?? undefined,
        });
        return;
    }

    navigate(fallbackPath, { replace: true });
};

export const useSafeBackNavigation = (fallbackRoute: string = '/') => {
    const navigate = useNavigate();
    const location = useLocation();

    const goBack = () => {
        if (hasModalBackground(location)) {
            exitToBackgroundPage(navigate, location, fallbackRoute);
            return;
        }

        const canGoBackInHistory = typeof window !== 'undefined' && window.history.length > 1;

        if (canGoBackInHistory) {
            navigate(-1);
            return;
        }

        navigate(fallbackRoute, { replace: true });
    };

    return { goBack };
};
